import { FC, memo } from "react"
import classNames from "classnames"
import { motion } from "framer-motion"
import ScrollHorizontalContainer from "@/components/Alpha/ScrollHorizontalContainer"
import IconButton from "@/components/IconButton"
import useClientStore from "@/lib/clientStore"

export interface TabDataRecipe {
  id: string
  title: string
  icon?: FC<{ width: number; height: number }>
}

interface RecipeCategoriesProps {
  tabDataRecipesWithIcon: TabDataRecipe[]
  category: string
  onSelectCategory: (categoryId: string) => void
  tabQuery: string
}

const RecipeCategories: FC<RecipeCategoriesProps> = ({
  tabDataRecipesWithIcon,
  category,
  onSelectCategory,
  tabQuery,
}) => {
  const isOpenSidebar = useClientStore(state => state.isOpenSidebar)

  // Early return if not on recommended tab
  if (tabQuery !== "recommended") return null

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex items-center w-full py-2 overflow-hidden mb-2"
    >
      <div className="flex justify-center w-full max-w-[100rem]">
        <div className="relative overflow-hidden flex justify-center py-4">
          <div
            className={classNames("overflow-hidden w-full", {
              "max-w-full": !isOpenSidebar,
              "lg:max-w-[calc(100vw-24rem)]": isOpenSidebar,
            })}
          >
            <ScrollHorizontalContainer className="flex h-full pb-4 space-x-2">
              {tabDataRecipesWithIcon
                .filter(i => i.id !== "All" && i.id !== "my-recipes")
                .map(tab => (
                  <CategoryButton key={tab.id} tab={tab} isSelected={tab.id === category} onSelect={onSelectCategory} />
                ))}
            </ScrollHorizontalContainer>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

interface CategoryButtonProps {
  tab: TabDataRecipe
  isSelected: boolean
  onSelect: (categoryId: string) => void
}

const CategoryButton: FC<CategoryButtonProps> = memo(({ tab, isSelected, onSelect }) => {
  const handleClick = () => {
    if (!isSelected) {
      onSelect(tab.id)
    }
  }

  return (
    <IconButton
      colorScheme="transparent"
      onClick={handleClick}
      className="flex items-center flex-col cursor-pointer shrink-0 group min-w-[7rem]"
    >
      <div
        className={classNames(
          "bg-atherGray-850 group-hover:bg-atherPurple-500 w-16 h-16 flex items-center justify-center rounded-3xl",
          {
            "bg-atherPurple-500": isSelected,
          },
        )}
      >
        {tab.icon && <tab.icon width={24} height={24} />}
      </div>
      <p className="text-sm font-semibold mt-2">{tab.title}</p>
    </IconButton>
  )
})

CategoryButton.displayName = "CategoryButton"

export default memo(RecipeCategories, (prevProps, nextProps) => {
  return (
    prevProps.tabDataRecipesWithIcon === nextProps.tabDataRecipesWithIcon &&
    prevProps.category === nextProps.category &&
    prevProps.tabQuery === nextProps.tabQuery
  )
})
